// src/components/Button.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const ButtonStyles = `
  display: inline-block;
  padding: 0.8rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
`;

// Use motion.button directly
const StyledButton = styled(motion.button)`
  ${ButtonStyles}
  background-color: ${props => props.variant === 'outline' 
    ? 'transparent' 
    : props.theme.colors[props.color || 'primary']};
  color: ${props => props.variant === 'outline' 
    ? props.theme.colors[props.color || 'primary'] 
    : 'white'};
  border: ${props => props.variant === 'outline' 
    ? `2px solid ${props.theme.colors[props.color || 'primary']}` 
    : 'none'};
  
  &:hover {
    background-color: ${props => props.variant === 'outline' 
      ? props.theme.colors[props.color || 'primary'] 
      : props.theme.colors.primary};
    color: white;
  }
  
  &:disabled {
    background-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

// Create a styled Link component
const LinkButton = styled(Link)`
  ${ButtonStyles}
  background-color: ${props => props.variant === 'outline' 
    ? 'transparent' 
    : props.theme.colors[props.color || 'primary']};
  color: ${props => props.variant === 'outline' 
    ? props.theme.colors[props.color || 'primary'] 
    : 'white'};
  border: ${props => props.variant === 'outline' 
    ? `2px solid ${props.theme.colors[props.color || 'primary']}` 
    : 'none'};
  text-decoration: none;
  
  &:hover {
    background-color: ${props => props.variant === 'outline' 
      ? props.theme.colors[props.color || 'primary'] 
      : props.theme.colors.primary};
    color: white;
  }
`;

// Create a motion component that wraps LinkButton without using motion()
const MotionLinkButton = ({ to, variant, color, children, ...props }) => {
  return (
    <motion.div
      style={{ display: 'inline-block' }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      {...props}
    >
      <LinkButton to={to} variant={variant} color={color}>
        {children}
      </LinkButton>
    </motion.div>
  );
};

const Button = ({ children, to, variant, color, ...props }) => {
  const buttonMotion = {
    whileHover: { scale: 1.05 },
    whileTap: { scale: 0.95 }
  };

  if (to) {
    return (
      <MotionLinkButton
        to={to}
        variant={variant}
        color={color}
        {...props}
      >
        {children}
      </MotionLinkButton>
    );
  }
  
  return (
    <StyledButton
      variant={variant}
      color={color}
      {...buttonMotion}
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export default Button;